import { useUnits } from '../../units/UnitContext';
import { formatDualDimension } from '../../units/unitConversions';

const SCREEN_PITCH = 160;
const PROFILE_REPEATS = 3;
const FIRST_CREST_CENTER_X = 250;
const CREST_Y = 160;

const coordinate = value => Number(value.toFixed(3));
const formatMillimetres = value => `${Number(value).toFixed(3).replace(/\.?0+$/, '')} mm`;
const formatRange = (minimum, maximum) => `${formatMillimetres(minimum)}–${formatMillimetres(maximum)}`;

const VerticalDimensionWithLanding = ({
  dimension, x, y1, y2, extensionToX, landingY, landingEndX, labelX, labelY, value,
}) => {
  const landingAnchorY = landingY < y1 ? y1 : y2;
  return (
    <g className="metricProfileDimensionCallout" data-dimension-callout={dimension}>
      <line className="metricProfileExtension" x1={x} y1={y1} x2={extensionToX} y2={y1} />
      <line className="metricProfileExtension" x1={x} y1={y2} x2={extensionToX} y2={y2} />
      <line className="metricProfileDimensionLine" x1={x} y1={y1 + 8} x2={x} y2={y2 - 8} />
      <line className="metricProfileDimensionLanding" x1={x} y1={landingAnchorY} x2={x} y2={landingY} />
      <line className="metricProfileDimensionLanding" data-dimension={dimension} x1={x} y1={landingY} x2={landingEndX} y2={landingY} />
      <text className="metricProfileVerticalValue" x={labelX} y={labelY}>{dimension} = {value}</text>
    </g>
  );
};

const OutsideArrowSegment = props => <line className="metricProfileOutsideArrow" {...props} />;

const buildProfileMetrics = basicGeometry => {
  const scale = SCREEN_PITCH / Number(basicGeometry.pitchMm);
  const pitch = coordinate(Number(basicGeometry.pitchMm) * scale);
  const crestFlat = coordinate(Number(basicGeometry.crestFlatWidthP8Mm) * scale);
  const rootFlat = coordinate(Number(basicGeometry.rootFlatWidthP4Mm) * scale);
  const h = coordinate(Number(basicGeometry.fundamentalTriangleHeightMm) * scale);
  const h1 = coordinate(Number(basicGeometry.basicThreadHeightH1Mm) * scale);
  const threeH8 = coordinate(Number(basicGeometry.h3Over8Mm) * scale);
  const h4 = coordinate(Number(basicGeometry.hOver4Mm) * scale);
  const h8 = coordinate(Number(basicGeometry.hOver8Mm) * scale);
  const theoryTopY = coordinate(CREST_Y - h8);
  const fundamentalBottomY = coordinate(theoryTopY + h);
  const basicRootY = coordinate(CREST_Y + h1);
  const pitchLevelY = coordinate(CREST_Y + threeH8);
  const crestCenters = Array.from({ length: PROFILE_REPEATS + 1 }, (_, index) => coordinate(FIRST_CREST_CENTER_X + index * pitch));
  const rootCenters = Array.from({ length: PROFILE_REPEATS }, (_, index) => coordinate(FIRST_CREST_CENTER_X + pitch / 2 + index * pitch));

  return {
    scale, pitch, crestFlat, rootFlat, h, h1, threeH8, h4, h8,
    theoryTopY, fundamentalBottomY, basicRootY, pitchLevelY,
    crestCenters, rootCenters,
  };
};

const buildInternalProfileGeometry = basicGeometry => {
  const metrics = buildProfileMetrics(basicGeometry);
  const { crestCenters, rootCenters, crestFlat, rootFlat, basicRootY } = metrics;
  const firstX = coordinate(crestCenters[0] - crestFlat / 2);
  const commands = [`M${firstX} ${CREST_Y}`, `H${coordinate(crestCenters[0] + crestFlat / 2)}`];

  rootCenters.forEach((rootCenter, index) => {
    commands.push(
      `L${coordinate(rootCenter - rootFlat / 2)} ${basicRootY}`,
      `H${coordinate(rootCenter + rootFlat / 2)}`,
      `L${coordinate(crestCenters[index + 1] - crestFlat / 2)} ${CREST_Y}`,
      `H${coordinate(crestCenters[index + 1] + crestFlat / 2)}`
    );
  });

  const path = commands.join(' ');
  return {
    ...metrics,
    path,
    materialPath: `${path} V80 H${firstX} Z`,
    rootProfile: 'p4-flat',
    centralRootCenter: rootCenters[1],
    centralCrestCenter: crestCenters[2],
  };
};

const buildExternalProfileGeometry = (basicGeometry, externalDesignProfile) => {
  const metrics = buildProfileMetrics(basicGeometry);
  const { crestCenters, rootCenters, crestFlat, scale } = metrics;
  const radius = coordinate(Number(externalDesignProfile.fullRootRadiusRMm) * scale);
  const threadHeight = coordinate(Number(externalDesignProfile.threadHeightH3Mm) * scale);
  const rootBottomY = coordinate(CREST_Y + threadHeight);
  const circleCenterY = coordinate(rootBottomY - radius);
  const tangentOffsetX = coordinate(radius * Math.sqrt(3) / 2);
  const tangentY = coordinate(circleCenterY + radius / 2);
  const firstX = coordinate(crestCenters[0] - crestFlat / 2);
  const commands = [`M${firstX} ${CREST_Y}`, `H${coordinate(crestCenters[0] + crestFlat / 2)}`];

  rootCenters.forEach((rootCenter, index) => {
    commands.push(
      `L${coordinate(rootCenter - tangentOffsetX)} ${tangentY}`,
      `A${radius} ${radius} 0 0 0 ${coordinate(rootCenter + tangentOffsetX)} ${tangentY}`,
      `L${coordinate(crestCenters[index + 1] - crestFlat / 2)} ${CREST_Y}`,
      `H${coordinate(crestCenters[index + 1] + crestFlat / 2)}`
    );
  });

  const path = commands.join(' ');
  return {
    ...metrics,
    path,
    materialPath: `${path} V330 H${firstX} Z`,
    rootProfile: 'rounded-r',
    radius,
    rootBottomY,
    tangentY,
    tangentOffsetX,
    centralRootCenter: rootCenters[1],
    centralCrestCenter: crestCenters[2],
  };
};

const MetricThreadProfile = ({
  result,
  preDrillDiameterMm,
  showGeometry = false,
  onGeometryToggle,
  geometryControlsId,
  expandedPreDrillTechnology = null,
  onPreDrillTechnologyToggle,
  cuttingPreDrillAvailable = false,
  formingPreDrillAvailable = false,
}) => {
  const { unitSystem } = useUnits();
  const internal = result.input.side === 'internal';
  const geometry = result.basicGeometry;
  const externalGeometry = result.externalDesignProfile;
  const profile = internal
    ? buildInternalProfileGeometry(geometry)
    : buildExternalProfileGeometry(geometry, externalGeometry);
  const geometryValue = value => formatDualDimension(value, unitSystem).primary;
  const tolerancePitch = formatMillimetres(geometry.pitchMm);
  const preDrillValue = Number.isFinite(Number(preDrillDiameterMm))
    ? formatDualDimension(preDrillDiameterMm, unitSystem).primary
    : '';
  const patternId = `metric-profile-hatch-${internal ? 'internal' : 'external'}`;
  const centralCrestLeft = coordinate(profile.centralCrestCenter - profile.crestFlat / 2);
  const centralCrestRight = coordinate(profile.centralCrestCenter + profile.crestFlat / 2);
  const centralRootLeft = coordinate(profile.centralRootCenter - profile.rootFlat / 2);
  const centralRootRight = coordinate(profile.centralRootCenter + profile.rootFlat / 2);
  const diameterCallouts = internal ? [
    { symbol: 'D', name: 'Major diameter', value: result.majorDiameter.maximumStatus === 'not_specified' ? `≥ ${formatMillimetres(result.majorDiameter.minimumMm)}` : formatRange(result.majorDiameter.minimumMm, result.majorDiameter.maximumMm), y: CREST_Y, labelY: 118 },
    { symbol: 'D₂', name: 'Pitch diameter', value: formatRange(result.pitchDiameter.minimumMm, result.pitchDiameter.maximumMm), y: profile.pitchLevelY, labelY: 213 },
    { symbol: 'D₁', name: 'Minor diameter', value: formatRange(result.minorDiameter.minimumMm, result.minorDiameter.maximumMm), y: profile.basicRootY, labelY: 308 },
  ] : [
    { symbol: 'd', name: 'Major diameter', value: formatRange(result.majorDiameter.minimumMm, result.majorDiameter.maximumMm), y: CREST_Y, labelY: 118 },
    { symbol: 'd₂', name: 'Pitch diameter', value: formatRange(result.pitchDiameter.minimumMm, result.pitchDiameter.maximumMm), y: profile.pitchLevelY, labelY: 213 },
    {
      symbol: 'd₃',
      name: 'Root diameter',
      value: formatRange(
        result.rootDiameter.minimumMm,
        result.rootDiameter.maximumMm ?? result.rootDiameter.designH6Mm
      ),
      y: profile.rootBottomY,
      labelY: 308,
    },
  ];

  return (
    <section className={`metricThreadProfile ${internal ? 'is-internal' : 'is-external'}`} aria-labelledby="metric-profile-heading">
      <div className="metricProfileHeading">
        <div>
          <p>60° ISO metric geometry</p>
          <h3 id="metric-profile-heading">Thread profile</h3>
        </div>
        <span>{internal ? 'Internal thread' : 'External thread'}</span>
      </div>

      <div className="metricProfileCanvas">
        <svg className={showGeometry ? 'hasGeometry' : undefined} viewBox={`0 0 ${showGeometry ? 1420 : 1100} 500`} role="img" aria-labelledby="metric-profile-title metric-profile-description">
          <title id="metric-profile-title">{internal ? 'Internal' : 'External'} metric thread profile for {result.designation}{showGeometry ? ' with geometry dimensions' : ''}</title>
          <desc id="metric-profile-description">{showGeometry
            ? `Three-pitch schematic of the canonical 60 degree ${internal ? 'internal profile with flat P over 4 roots' : 'external profile with circular R roots'}, with backend-provided construction dimensions. Not to scale.`
            : `Three-pitch schematic of the same canonical ${internal ? 'internal' : 'external'} profile with live diameter tolerance callouts. Not to scale.`}</desc>
          <defs>
            <pattern id={patternId} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line className="metricProfileHatch" x1="0" y1="0" x2="0" y2="12" />
            </pattern>
            <marker id="metric-profile-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path className="metricProfileArrow" d="M0 0 L10 5 L0 10 Z" />
            </marker>
          </defs>

          <path className="metricProfileMaterial" d={profile.materialPath} />
          <path className="metricProfilePattern" d={profile.materialPath} fill={`url(#${patternId})`} />
          <path
            className="metricProfileOutline"
            d={profile.path}
            data-testid="metric-profile-actual-geometry"
            data-profile-side={internal ? 'internal' : 'external'}
            data-profile-repeats={PROFILE_REPEATS}
            data-crest-flat="p8"
            data-root-profile={profile.rootProfile}
            data-root-curve={internal ? 'none' : 'circular-arc'}
          />

          <g className="metricProfilePitch">
            <line x1={profile.crestCenters[1]} y1={CREST_Y - 8} x2={profile.crestCenters[1]} y2="34" />
            <line x1={profile.crestCenters[2]} y1={CREST_Y - 8} x2={profile.crestCenters[2]} y2="34" />
            <line className="metricProfileDimension" x1={profile.crestCenters[1] + 9} y1="46" x2={profile.crestCenters[2] - 9} y2="46" />
            <text x={(profile.crestCenters[1] + profile.crestCenters[2]) / 2} y="29" textAnchor="middle">P = {showGeometry ? geometryValue(geometry.pitchMm) : tolerancePitch}</text>
          </g>

          <g className="metricProfileAngle metricProfileIncludedAngle">
            <line x1={profile.centralCrestCenter - 24} y1={CREST_Y + 49} x2={profile.centralCrestCenter - 10} y2={CREST_Y + 25} />
            <line x1={profile.centralCrestCenter + 10} y1={CREST_Y + 25} x2={profile.centralCrestCenter + 24} y2={CREST_Y + 49} />
            <text x={profile.centralCrestCenter} y={CREST_Y + 68} textAnchor="middle">60°</text>
          </g>

          <g className={`metricProfileCallouts${showGeometry ? ' is-geometry' : ''}`} data-testid="metric-profile-tolerance-callouts">
            {diameterCallouts.map(callout => {
              const lineStartX = showGeometry ? 770 : 220;
              const lineEndX = showGeometry ? 1130 : 770;
              const leaderEndX = showGeometry ? 1150 : 790;
              const labelX = showGeometry ? 1160 : 800;
              return (
                <g key={callout.symbol} data-diameter={callout.symbol}>
                  <line x1={lineStartX} y1={callout.y} x2={lineEndX} y2={callout.y} />
                  <line className="metricProfileCalloutLeader" x1={lineEndX} y1={callout.y} x2={leaderEndX} y2={callout.labelY} />
                  <text className="metricProfileSymbol" x={labelX} y={callout.labelY - 9}>{callout.symbol}</text>
                  <text className="metricProfileName" x={labelX + 35} y={callout.labelY - 9}>{callout.name}</text>
                  <text className="metricProfileValue" x={labelX} y={callout.labelY + 16}>{callout.value}</text>
                </g>
              );
            })}
          </g>

          {internal && preDrillValue && (
            <g className={`metricProfilePreDrill${showGeometry ? ' is-geometry' : ''}`} data-testid="metric-profile-pre-drill-annotation">
              <line x1={showGeometry ? 770 : 220} y1={profile.basicRootY} x2={showGeometry ? 1080 : 720} y2={profile.basicRootY} />
              <line className="metricProfilePreDrillLeader" x1={showGeometry ? 1080 : 720} y1={profile.basicRootY} x2={showGeometry ? 1125 : 765} y2="432" />
              <text className="metricProfilePreDrillLabel" x={showGeometry ? 1140 : 780} y="420">Recommended pre-drill Ø</text>
              <text className="metricProfilePreDrillValue" x={showGeometry ? 1140 : 780} y="444">{preDrillValue}</text>
            </g>
          )}

          {showGeometry && (
            <g className="metricProfileGeometryLayer" data-testid="metric-profile-geometry-dimensions">
              <g className="metricProfileConstruction">
                <line x1={profile.centralRootCenter} y1={profile.fundamentalBottomY} x2={profile.centralCrestCenter} y2={profile.theoryTopY} />
                <line x1={profile.centralCrestCenter} y1={profile.theoryTopY} x2={profile.rootCenters[2]} y2={profile.fundamentalBottomY} />
                {[profile.theoryTopY, CREST_Y, profile.pitchLevelY, profile.basicRootY, profile.fundamentalBottomY].map(level => (
                  <line key={level} x1="215" y1={level} x2="775" y2={level} />
                ))}
                {!internal && (
                  <line className="metricProfileBasicFlat is-theoretical" data-testid="metric-profile-basic-p4-construction" x1={centralRootLeft} y1={profile.basicRootY} x2={centralRootRight} y2={profile.basicRootY} />
                )}
              </g>

              <g className="metricProfileGeometryDimension metricProfileGeometryHorizontal">
                <line className="metricProfileExtension" x1={profile.centralRootCenter} y1={profile.fundamentalBottomY} x2={profile.centralRootCenter} y2="73" />
                <line className="metricProfileExtension" x1={profile.centralCrestCenter} y1={profile.theoryTopY} x2={profile.centralCrestCenter} y2="73" />
                <line className="metricProfileDimensionLine" x1={profile.centralRootCenter + 8} y1="81" x2={profile.centralCrestCenter - 8} y2="81" />
                <line className="metricProfileDimensionLanding" data-dimension="P/2" x1={profile.centralCrestCenter} y1="81" x2="700" y2="81" />
                <text x="590" y="77">P/2 = {geometryValue(geometry.halfPitchMm)}</text>

                <line className="metricProfileExtension" x1={centralCrestLeft} y1={CREST_Y} x2={centralCrestLeft} y2="126" />
                <line className="metricProfileExtension" x1={centralCrestRight} y1={CREST_Y} x2={centralCrestRight} y2="126" />
                <OutsideArrowSegment x1={centralCrestLeft - 24} y1="134" x2={centralCrestLeft - 4} y2="134" />
                <OutsideArrowSegment x1={centralCrestRight + 112} y1="134" x2={centralCrestRight + 4} y2="134" />
                <text className="metricProfileHorizontalValue" x={centralCrestRight + 24} y="130">P/8 = {geometryValue(geometry.crestFlatWidthP8Mm)}</text>

                <line className="metricProfileExtension" x1={centralRootLeft} y1={profile.basicRootY} x2={centralRootLeft} y2="367" />
                <line className="metricProfileExtension" x1={centralRootRight} y1={profile.basicRootY} x2={centralRootRight} y2="367" />
                <line className="metricProfileDimensionLine" x1={centralRootLeft + 6} y1="359" x2={centralRootRight - 6} y2="359" />
                <text className={!internal ? 'metricProfileBasicLabel' : undefined} x={profile.centralRootCenter} y="383" textAnchor="middle">P/4 = {geometryValue(geometry.rootFlatWidthP4Mm)}{internal ? '' : ' · basic profile'}</text>
              </g>

              <g className="metricProfileGeometryDimension metricProfileGeometryVertical metricProfileGeometryLeftLanes">
                <VerticalDimensionWithLanding
                  dimension="H" x={55} y1={profile.theoryTopY} y2={profile.fundamentalBottomY}
                  extensionToX={profile.centralRootCenter} landingY={333} landingEndX={170}
                  labelX={70} labelY={329} value={geometryValue(geometry.fundamentalTriangleHeightMm)}
                />
                <VerticalDimensionWithLanding
                  dimension="H1" x={115} y1={CREST_Y} y2={profile.basicRootY}
                  extensionToX={profile.centralRootCenter} landingY={311} landingEndX={225}
                  labelX={130} labelY={307} value={geometryValue(geometry.basicThreadHeightH1Mm)}
                />
                <VerticalDimensionWithLanding
                  dimension="3H/8" x={175} y1={CREST_Y} y2={profile.pitchLevelY}
                  extensionToX={profile.centralRootCenter} landingY={internal ? 116 : 132} landingEndX={310}
                  labelX={195} labelY={internal ? 112 : 128} value={geometryValue(geometry.h3Over8Mm)}
                />
              </g>

              <g className="metricProfileGeometryDimension metricProfileGeometryVertical metricProfileGeometryRightLanes">
                <line className="metricProfileExtension" x1={profile.rootCenters[2]} y1={profile.basicRootY} x2="825" y2={profile.basicRootY} />
                <line className="metricProfileExtension" x1={profile.rootCenters[2]} y1={profile.fundamentalBottomY} x2="825" y2={profile.fundamentalBottomY} />
                <line className="metricProfileDimensionLine" x1="825" y1={profile.basicRootY + 7} x2="825" y2={profile.fundamentalBottomY - 7} />
                <text x="850" y={(profile.basicRootY + profile.fundamentalBottomY) / 2 + 14}>H/4 = {geometryValue(geometry.hOver4Mm)}</text>

                <line className="metricProfileExtension" x1={profile.rootCenters[2]} y1={profile.theoryTopY} x2="945" y2={profile.theoryTopY} />
                <line className="metricProfileExtension" x1={profile.rootCenters[2]} y1={CREST_Y} x2="945" y2={CREST_Y} />
                <OutsideArrowSegment x1="945" y1={profile.theoryTopY - 24} x2="945" y2={profile.theoryTopY - 4} />
                <OutsideArrowSegment x1="945" y1={CREST_Y + 24} x2="945" y2={CREST_Y + 4} />
                <line className="metricProfileDimensionLanding" data-dimension="H/8" x1="945" y1={(profile.theoryTopY + CREST_Y) / 2} x2="1090" y2={(profile.theoryTopY + CREST_Y) / 2} />
                <text x="960" y={(profile.theoryTopY + CREST_Y) / 2 - 4}>H/8 = {geometryValue(geometry.hOver8Mm)}</text>
              </g>

              <g className="metricProfileFlankAngle">
                <line x1={profile.crestCenters[3] - 55} y1={CREST_Y + 72} x2={profile.crestCenters[3]} y2={CREST_Y + 72} />
                <line x1={profile.crestCenters[3] - 55} y1={CREST_Y + 72} x2={profile.crestCenters[3] - 31} y2={CREST_Y + 31} />
                <text x={profile.crestCenters[3] + 6} y={CREST_Y + 68}>30°</text>
              </g>

              {!internal && externalGeometry && (
                <g className="metricProfileExternalGeometry">
                  <line className="metricProfileExtension" x1={profile.crestCenters[3] + profile.crestFlat / 2} y1={CREST_Y} x2="1030" y2={CREST_Y} />
                  <line className="metricProfileExtension" x1={profile.centralRootCenter} y1={profile.rootBottomY} x2="1030" y2={profile.rootBottomY} />
                  <line className="metricProfileDimensionLine" x1="1030" y1={CREST_Y + 8} x2="1030" y2={profile.rootBottomY - 8} />
                  <text x="1000" y={(CREST_Y + profile.rootBottomY) / 2 - 8} textAnchor="end">h3 = {geometryValue(externalGeometry.threadHeightH3Mm)}</text>

                  <line className="metricProfileRadiusLeader" data-dimension="R" x1={profile.rootCenters[0]} y1={profile.rootBottomY} x2="170" y2="420" />
                  <text x="55" y="424">R = {geometryValue(externalGeometry.fullRootRadiusRMm)}</text>
                  <g className="metricProfileSecondaryRadius" data-testid="metric-profile-r1-note">
                    <line className="metricProfileRadiusLeader is-minimum" data-dimension="R1,min" x1={profile.rootCenters[0] + profile.tangentOffsetX} y1={profile.tangentY} x2="205" y2="440" />
                    <text x="225" y="444">R1,min = {geometryValue(externalGeometry.minimumPartialRootRadiusR1Mm)}</text>
                    <text className="metricProfileGeometryQualifier" x="225" y="462">minimum partial-root radius · secondary</text>
                  </g>
                </g>
              )}
            </g>
          )}

        </svg>
      </div>
      <div className="metricProfileFooter">
        <span className="metricProfileScaleNote">Schematic · not to scale</span>
        {internal && (
          <button
            className="metricProfileTechnologyToggle"
            type="button"
            aria-expanded={expandedPreDrillTechnology === 'cutting'}
            aria-controls="metric-cutting-pre-drill-results"
            disabled={!cuttingPreDrillAvailable}
            onClick={() => onPreDrillTechnologyToggle?.('cutting')}
          >
            <span>Cutting Tap / Thread Milling</span>
            <span aria-hidden="true">{expandedPreDrillTechnology === 'cutting' ? '−' : '+'}</span>
          </button>
        )}
        {internal && (
          <button
            className="metricProfileTechnologyToggle"
            type="button"
            aria-expanded={expandedPreDrillTechnology === 'forming'}
            aria-controls="metric-forming-pre-drill-results"
            disabled={!formingPreDrillAvailable}
            onClick={() => onPreDrillTechnologyToggle?.('forming')}
          >
            <span>Forming Tap</span>
            <span aria-hidden="true">{expandedPreDrillTechnology === 'forming' ? '−' : '+'}</span>
          </button>
        )}
        <button
          className="metricProfileGeometryToggle"
          type="button"
          aria-expanded={showGeometry}
          aria-controls={geometryControlsId}
          onClick={onGeometryToggle}
        >
          {showGeometry ? '− Hide geometry' : '+ Show geometry'}
        </button>
      </div>
    </section>
  );
};

export default MetricThreadProfile;
